import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Eye, FilePlus2, Play, RefreshCcw, Save, Search, ShieldAlert } from "lucide-react";
import api from "../../services/api";
import { useUser } from "../../services/context/UserContext";
import StatusBadge from "../ui/StatusBadge";
import {
  MANAGEABLE_PRICING_SCOPE_OPTIONS,
  computePricingAgreementStats,
  formatPricingDateTime,
  formatPricingValidity,
  getPricingAgreementDerivedStatus,
  getPricingScopeLabel,
  hasFinancialPricingAccess,
} from "../../utils/pricingUi";

export const SCOPE_OPTIONS = MANAGEABLE_PRICING_SCOPE_OPTIONS;

const LINE_TYPE_OPTIONS = [
  { value: "labor", label: "Arbeid" },
  { value: "material", label: "Materiaal" },
  { value: "other", label: "Overig" },
];

const FORMULA_OPTIONS = [
  { value: "inherit", label: "Overnemen" },
  { value: "fixed", label: "Vast bedrag" },
  { value: "discount_pct", label: "Korting %" },
  { value: "markup_pct", label: "Opslag %" },
  { value: "cost_plus_pct", label: "Kost + %" },
  { value: "cost_plus_fixed", label: "Kost + vast" },
];

const EMPTY_RULE = {
  name: "",
  code: "",
  lineType: "labor",
  active: true,
  priority: 100,
  match: {
    workType: "",
    hourTypeId: "",
    materialCatalogKey: "",
    materialCategory: "",
    otherCostCategory: "",
    minQuantity: "",
    maxQuantity: "",
  },
  sellFormula: {
    mode: "inherit",
    value: "",
    minimumAmount: "",
    label: "",
  },
  costFormula: {
    mode: "inherit",
    value: "",
    minimumAmount: "",
    label: "",
  },
  notes: "",
};

const EMPTY_RESOLVE_FORM = {
  projectId: "",
  pricingProfileId: "",
  pricingDate: new Date().toISOString().slice(0, 10),
  lineType: "labor",
  quantity: "1",
  workType: "",
  hourTypeId: "",
  materialCatalogKey: "",
  materialCategory: "",
  otherCostCategory: "",
  sellRate: "",
  costRate: "",
  costInput: "",
  discount: "",
  markup: "",
  amountSell: "",
  amountCost: "",
};

export default function ClientPricingTab({ client, data, rawClient }) {
  const { user } = useUser();
  const permissionSet = useMemo(
    () =>
      new Set(
        (Array.isArray(user?.permissions) ? user.permissions : [])
          .map((item) => String(item || "").trim().toLowerCase())
          .filter(Boolean)
      ),
    [user?.permissions]
  );
  const canView = permissionSet.has("pricing.view") || permissionSet.has("pricing.manage");
  const canManage = permissionSet.has("pricing.manage");
  const showFinancials = useMemo(() => hasFinancialPricingAccess(user?.permissions), [user?.permissions]);

  const clientId = String(client?.id || rawClient?._id || "").trim();
  const projects = useMemo(() => (Array.isArray(data?.projects) ? data.projects : []), [data?.projects]);
  const projectIdSet = useMemo(
    () => new Set(projects.map((item) => String(item?.id || item?._id || "")).filter(Boolean)),
    [projects]
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agreements, setAgreements] = useState([]);
  const [hourTypes, setHourTypes] = useState([]);
  const [filters, setFilters] = useState({
    status: "",
    scopeType: "",
    validOn: new Date().toISOString().slice(0, 10),
    search: "",
  });
  const [detail, setDetail] = useState(null);
  const [auditEntries, setAuditEntries] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editorMode, setEditorMode] = useState("");
  const [editorForm, setEditorForm] = useState(buildAgreementForm({ clientId }));
  const [assignmentAgreementId, setAssignmentAgreementId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [resolveForm, setResolveForm] = useState(EMPTY_RESOLVE_FORM);
  const [resolveLoading, setResolveLoading] = useState(false);
  const [resolveResult, setResolveResult] = useState(null);
  const [assignedProfileScopeRefState, setAssignedProfileScopeRefState] = useState(String(rawClient?.pricingProfileId || "").trim());

  const assignedProfileScopeRef = assignedProfileScopeRefState;

  const relevantAgreements = useMemo(() => {
    return agreements.filter((item) => {
      const scopeType = String(item?.scopeType || "").trim().toLowerCase();
      const scopeRef = String(item?.scopeRef || "").trim();
      if (scopeType === "tenant_default") return true;
      if (scopeType === "client") return scopeRef === clientId;
      if (scopeType === "project") return projectIdSet.has(scopeRef);
      if (scopeType === "pricing_profile") return true;
      return false;
    });
  }, [agreements, clientId, projectIdSet]);

  const stats = useMemo(
    () => computePricingAgreementStats(relevantAgreements, filters.validOn || new Date()),
    [filters.validOn, relevantAgreements]
  );

  const profileOptions = useMemo(() => {
    const latestByScopeRef = new Map();
    for (const agreement of relevantAgreements.filter((item) => item?.scopeType === "pricing_profile")) {
      const key = String(agreement.scopeRef || "");
      if (!key) continue;
      const current = latestByScopeRef.get(key);
      if (!current || Number(agreement.version || 0) > Number(current.version || 0)) {
        latestByScopeRef.set(key, agreement);
      }
    }
    return [...latestByScopeRef.values()].sort((left, right) =>
      String(left.name || "").localeCompare(String(right.name || ""))
    );
  }, [relevantAgreements]);

  const loadPricingData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = new URLSearchParams();
      if (filters.status) params.set("status", filters.status);
      if (filters.scopeType) params.set("scopeType", filters.scopeType);
      if (filters.validOn) params.set("validOn", filters.validOn);
      if (filters.search) params.set("search", filters.search);
      if (clientId) params.set("clientId", clientId);
      if (canManage) params.set("includePricingProfiles", "all");

      const [agreementRes, hourTypeRes] = await Promise.all([
        api.get(`/pricing-agreements${params.toString() ? `?${params.toString()}` : ""}`),
        api.get("/tenant-hour-types"),
      ]);

      setAgreements(Array.isArray(agreementRes?.data) ? agreementRes.data : []);
      setHourTypes(Array.isArray(hourTypeRes?.data) ? hourTypeRes.data : []);
    } catch (err) {
      setError(err?.response?.data?.message || "Prijsafspraken laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [canManage, clientId, filters.scopeType, filters.search, filters.status, filters.validOn]);

  useEffect(() => {
    if (!canView || !clientId) return;
    loadPricingData();
  }, [canView, clientId, loadPricingData]);

  useEffect(() => {
    const matchingAssignedProfile = profileOptions.find(
      (item) => String(item.scopeRef || "") === String(assignedProfileScopeRef || "")
    );
    setAssignmentAgreementId(matchingAssignedProfile?._id || "");
  }, [assignedProfileScopeRef, profileOptions]);

  useEffect(() => {
    setAssignedProfileScopeRefState(String(rawClient?.pricingProfileId || "").trim());
  }, [rawClient?.pricingProfileId]);

  async function openDetail(agreementId) {
    if (!agreementId) return;
    try {
      setDetailLoading(true);
      setError("");
      const [detailRes, auditRes] = await Promise.all([
        api.get(`/pricing-agreements/${agreementId}`),
        api.get(`/pricing-agreements/${agreementId}/audit`),
      ]);
      setDetail(detailRes?.data || null);
      setAuditEntries(Array.isArray(auditRes?.data?.entries) ? auditRes.data.entries : []);
      setEditorMode("");
    } catch (err) {
      setError(err?.response?.data?.message || "Prijsafspraakdetail laden mislukt");
    } finally {
      setDetailLoading(false);
    }
  }

  function startCreate() {
    setEditorMode("create");
    setDetail(null);
    setAuditEntries([]);
    setEditorForm(buildAgreementForm({ clientId }));
    setMessage("");
    setError("");
  }

  function startEdit(agreement) {
    setEditorMode("edit");
    setEditorForm(buildAgreementFormFromAgreement(agreement));
    setMessage("");
    setError("");
  }

  function startRevise(agreement) {
    setEditorMode("revise");
    setEditorForm(buildAgreementFormFromAgreement(agreement));
    setMessage("");
    setError("");
  }

  async function submitEditor(event) {
    event.preventDefault();
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const payload = serializeAgreementForm(editorForm, clientId);
      let response = null;
      if (editorMode === "create") {
        response = await api.post("/pricing-agreements", payload);
      } else if (editorMode === "edit" && editorForm.id) {
        response = await api.put(`/pricing-agreements/${editorForm.id}`, payload);
      } else if (editorMode === "revise" && editorForm.id) {
        response = await api.post(`/pricing-agreements/${editorForm.id}/revise`, payload);
      }

      await loadPricingData();
      if (response?.data?._id) {
        await openDetail(response.data._id);
      }
      setEditorMode("");
      setMessage("Prijsafspraak opgeslagen.");
    } catch (err) {
      setError(err?.response?.data?.message || "Prijsafspraak opslaan mislukt");
    } finally {
      setSaving(false);
    }
  }

  async function runAgreementAction(agreementId, action, body = {}, successMessage = "") {
    try {
      setSaving(true);
      setError("");
      setMessage("");
      await api.post(`/pricing-agreements/${agreementId}/${action}`, body);
      await loadPricingData();
      await openDetail(agreementId);
      setMessage(successMessage);
    } catch (err) {
      setError(err?.response?.data?.message || "Actie op prijsafspraak mislukt");
    } finally {
      setSaving(false);
    }
  }

  async function saveAssignment() {
    try {
      setSaving(true);
      setError("");
      setMessage("");
      await api.put(`/pricing-agreements/assignments/clients/${clientId}`, {
        pricingProfileId: assignmentAgreementId || null,
      });
      const selectedProfile = profileOptions.find((item) => String(item._id) === String(assignmentAgreementId));
      setAssignedProfileScopeRefState(String(selectedProfile?.scopeRef || ""));
      setMessage("Prijsprofieltoewijzing bijgewerkt.");
    } catch (err) {
      setError(err?.response?.data?.message || "Prijsprofiel toewijzen mislukt");
    } finally {
      setSaving(false);
    }
  }

  async function runResolvePreview(event) {
    event.preventDefault();
    try {
      setResolveLoading(true);
      setResolveResult(null);
      setError("");
      const payload = buildResolvePayload({
        clientId,
        assignedProfileScopeRef,
        form: resolveForm,
        includeFinancials: showFinancials,
      });
      const response = await api.post("/pricing-agreements/resolve-preview", payload);
      setResolveResult(response?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || "Prijscontrole mislukt");
    } finally {
      setResolveLoading(false);
    }
  }

  if (!canView) {
    return (
      <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
        Je hebt geen toegang tot prijsafspraken voor deze klant.
      </section>
    );
  }

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Commercieel</p>
            <h3 className="mt-2 text-xl font-semibold text-slate-950">Prijsafspraken</h3>
            <p className="mt-1 max-w-3xl text-sm text-slate-600">
              Beheer tenantstandaard, prijsprofielen, klant- en projectafspraken rond deze klant zonder een tweede prijsmodel naast de live resolver te bouwen.
            </p>
          </div>
          {canManage ? (
            <button
              type="button"
              onClick={startCreate}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              <FilePlus2 size={16} />
              Nieuwe prijsafspraak
            </button>
          ) : null}
        </div>

        {message ? <p className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{message}</p> : null}
        {error ? <p className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{error}</p> : null}

        <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <StatCard label="Actief" value={stats.active} tone="success" />
          <StatCard label="Concept" value={stats.draft} tone="neutral" />
          <StatCard label="Verlopen" value={stats.expired} tone="warning" />
          <StatCard label="Ingetrokken" value={stats.archived} tone="danger" />
          <StatCard label="Waarschuwingen" value={stats.warnings} tone="warning" />
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_1.2fr_auto]">
          <Field label="Status">
            <select
              value={filters.status}
              onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
            >
              <option value="">Alle statussen</option>
              <option value="active">Actief</option>
              <option value="draft">Concept</option>
              <option value="expired">Verlopen</option>
              <option value="archived">Ingetrokken</option>
            </select>
          </Field>

          <Field label="Niveau">
            <select
              value={filters.scopeType}
              onChange={(event) => setFilters((prev) => ({ ...prev, scopeType: event.target.value }))}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
            >
              <option value="">Alle niveaus</option>
              {SCOPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Geldig op">
            <input
              type="date"
              value={filters.validOn}
              onChange={(event) => setFilters((prev) => ({ ...prev, validOn: event.target.value }))}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
            />
          </Field>

          <Field label="Zoeken">
            <div className="relative">
              <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={filters.search}
                onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
                placeholder="Naam of omschrijving"
                className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-900"
              />
            </div>
          </Field>

          <div className="flex items-end">
            <button
              type="button"
              onClick={loadPricingData}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              <RefreshCcw size={16} />
              Verversen
            </button>
          </div>
        </div>
      </section>

      {canManage ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Prijsprofiel voor deze klant</h4>
              <p className="mt-1 text-sm text-slate-600">
                Koppel desgewenst een prijsprofiel. De resolver gebruikt dit alleen wanneer er geen projectspecifieke of klantspecifieke prijsafspraak wint.
              </p>
            </div>
            <div className="grid w-full gap-3 xl:w-[560px] xl:grid-cols-[1fr_auto]">
              <select
                value={assignmentAgreementId}
                onChange={(event) => setAssignmentAgreementId(event.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
              >
                <option value="">Geen prijsprofiel gekoppeld</option>
                {profileOptions.map((option) => {
                  const derived = getPricingAgreementDerivedStatus(option, filters.validOn || new Date());
                  return (
                    <option key={option._id} value={option._id}>
                      {option.name} · v{option.version || 1} · {derived.label}
                    </option>
                  );
                })}
              </select>
              <button
                type="button"
                onClick={saveAssignment}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
              >
                <Save size={16} />
                Opslaan
              </button>
            </div>
          </div>
        </section>
      ) : null}

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-5 py-4">
          <h4 className="text-sm font-semibold text-slate-900">Overzicht</h4>
          <p className="mt-1 text-sm text-slate-600">Relevante prijsafspraken voor deze klant, inclusief tenantstandaard en prijsprofielen.</p>
        </div>

        {loading ? <p className="px-5 py-8 text-sm text-slate-500">Prijsafspraken laden...</p> : null}
        {!loading && relevantAgreements.length === 0 ? (
          <p className="px-5 py-8 text-sm text-slate-500">Nog geen relevante prijsafspraken gevonden.</p>
        ) : null}

        {!loading && relevantAgreements.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2 font-semibold">Naam</th>
                  <th className="px-4 py-2 font-semibold">Niveau</th>
                  <th className="px-4 py-2 font-semibold">Context</th>
                  <th className="px-4 py-2 font-semibold">Status</th>
                  <th className="px-4 py-2 font-semibold">Geldigheid</th>
                  <th className="px-4 py-2 font-semibold">Versie</th>
                  <th className="px-4 py-2 font-semibold">Regels</th>
                  <th className="px-4 py-2 font-semibold">Gewijzigd</th>
                  <th className="px-4 py-2 font-semibold">Acties</th>
                </tr>
              </thead>
              <tbody>
                {relevantAgreements.map((agreement) => {
                  const derived = getPricingAgreementDerivedStatus(agreement, filters.validOn || new Date());
                  return (
                    <tr key={agreement._id} className="border-b border-slate-100 align-top">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{agreement.name || "-"}</div>
                        <div className="text-xs text-slate-500">{agreement.description || "Geen omschrijving"}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <div>{getPricingScopeLabel(agreement.scopeType)}</div>
                        {agreement.scopeType === "pricing_profile" && String(agreement.scopeRef || "") === assignedProfileScopeRef ? (
                          <StatusBadge tone="info" label="Gekoppeld aan klant" className="mt-1" />
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <div>{agreement?.contextSummary?.title || "-"}</div>
                        <div className="text-xs text-slate-500">{agreement?.contextSummary?.subtitle || "-"}</div>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge tone={mapDerivedStatusTone(derived.key)} label={derived.label} />
                        {Array.isArray(agreement?.warnings) && agreement.warnings.length > 0 ? (
                          <div className="mt-2 text-xs text-amber-700">{agreement.warnings.length} waarschuwing(en)</div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{formatPricingValidity(agreement)}</td>
                      <td className="px-4 py-3 text-slate-700">
                        <div>v{agreement.version || 1}</div>
                        <div className="text-xs text-slate-500">Serie {String(agreement.versionSeriesId || "").slice(-6).toUpperCase() || "-"}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{agreement.ruleCount || 0}</td>
                      <td className="px-4 py-3 text-slate-700">{formatPricingDateTime(agreement.lastChangedAt || agreement.updatedAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => openDetail(agreement._id)}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
                          >
                            <Eye size={14} />
                            Bekijk
                          </button>
                          {canManage && agreement.status === "draft" ? (
                            <button
                              type="button"
                              onClick={() => startEdit(agreement)}
                              className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
                            >
                              Bewerken
                            </button>
                          ) : null}
                          {canManage ? (
                            <button
                              type="button"
                              onClick={() => startRevise(agreement)}
                              className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
                            >
                              Nieuwe versie
                            </button>
                          ) : null}
                          {canManage && agreement.status === "draft" ? (
                            <button
                              type="button"
                              onClick={() => runAgreementAction(agreement._id, "activate", {}, "Prijsafspraak geactiveerd.")}
                              className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-800 transition hover:bg-emerald-100"
                            >
                              <Play size={14} />
                              Activeren
                            </button>
                          ) : null}
                          {canManage && agreement.status !== "archived" ? (
                            <button
                              type="button"
                              onClick={() => runAgreementAction(agreement._id, "retract", { reason: "Ingetrokken via klanthub." }, "Prijsafspraak ingetrokken.")}
                              className="rounded-md border border-rose-300 bg-rose-50 px-2.5 py-1.5 text-xs font-medium text-rose-800 transition hover:bg-rose-100"
                            >
                              Intrekken
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {editorMode ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">
                {editorMode === "create" ? "Nieuwe prijsafspraak" : editorMode === "edit" ? "Concept bewerken" : "Nieuwe versie maken"}
              </h4>
              <p className="mt-1 text-sm text-slate-600">
                Gebruik alleen de bestaande scope-types en regelvelden die de live resolver al ondersteunt.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setEditorMode("")}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Sluiten
            </button>
          </div>

          <form onSubmit={submitEditor} className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Field label="Naam">
                <input
                  value={editorForm.name}
                  onChange={(event) => setEditorForm((prev) => ({ ...prev, name: event.target.value }))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                  required
                />
              </Field>
              <Field label="Scope">
                <select
                  value={editorForm.scopeType}
                  onChange={(event) => setEditorForm((prev) => updateScopeType(prev, event.target.value, clientId))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                >
                  {SCOPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Valuta">
                <input
                  value={editorForm.currency}
                  onChange={(event) => setEditorForm((prev) => ({ ...prev, currency: event.target.value.toUpperCase() }))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                />
              </Field>
              <Field label="Status">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">Concept</div>
              </Field>
              <Field label="Omschrijving" className="md:col-span-2 xl:col-span-4">
                <textarea
                  value={editorForm.description}
                  onChange={(event) => setEditorForm((prev) => ({ ...prev, description: event.target.value }))}
                  rows={3}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                />
              </Field>
              <Field label="Geldig vanaf">
                <input
                  type="date"
                  value={editorForm.effectiveFrom}
                  onChange={(event) => setEditorForm((prev) => ({ ...prev, effectiveFrom: event.target.value }))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                />
              </Field>
              <Field label="Geldig tot">
                <input
                  type="date"
                  value={editorForm.effectiveTo}
                  onChange={(event) => setEditorForm((prev) => ({ ...prev, effectiveTo: event.target.value }))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                />
              </Field>
              <Field label="Klantcontext">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  {editorForm.scopeType === "client" ? client?.bedrijfsnaam || "Huidige klant" : editorForm.scopeType === "tenant_default" ? "Niet van toepassing" : editorForm.scopeType === "pricing_profile" ? "Wordt via profielscope beheerd" : "Selecteer project"}
                </div>
              </Field>
              <Field label="Project">
                <select
                  value={editorForm.scopeRef}
                  onChange={(event) => setEditorForm((prev) => ({ ...prev, scopeRef: event.target.value }))}
                  disabled={editorForm.scopeType !== "project"}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 disabled:bg-slate-50"
                >
                  <option value="">{editorForm.scopeType === "project" ? "Kies project" : "Niet van toepassing"}</option>
                  {projects.map((project) => (
                    <option key={project.id || project._id} value={project.id || project._id}>
                      {project.projectNummer ? `${project.projectNummer} · ` : ""}{project.naam || project.title}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h5 className="text-sm font-semibold text-slate-900">Tariefregels</h5>
                  <p className="mt-1 text-sm text-slate-600">Arbeid, materiaal en overige kosten lopen door dezelfde live resolver.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditorForm((prev) => ({ ...prev, rules: [...prev.rules, cloneRule(EMPTY_RULE)] }))}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                >
                  Regel toevoegen
                </button>
              </div>

              {editorForm.rules.map((rule, index) => (
                <div key={rule.localId} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Regel {index + 1}</p>
                      <p className="text-xs text-slate-500">Werk alleen met velden die live al door PricingAgreement worden opgeslagen.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setEditorForm((prev) => ({
                          ...prev,
                          rules: prev.rules.filter((item) => item.localId !== rule.localId),
                        }))
                      }
                      className="rounded-md border border-rose-300 bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-700 transition hover:bg-rose-100"
                    >
                      Verwijderen
                    </button>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <Field label="Naam">
                      <input
                        value={rule.name}
                        onChange={(event) => updateRule(index, "name", event.target.value, setEditorForm)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                      />
                    </Field>
                    <Field label="Code">
                      <input
                        value={rule.code}
                        onChange={(event) => updateRule(index, "code", event.target.value, setEditorForm)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                      />
                    </Field>
                    <Field label="Type regel">
                      <select
                        value={rule.lineType}
                        onChange={(event) => updateRule(index, "lineType", event.target.value, setEditorForm)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                      >
                        {LINE_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Prioriteit">
                      <input
                        type="number"
                        min="0"
                        value={rule.priority}
                        onChange={(event) => updateRule(index, "priority", event.target.value, setEditorForm)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                      />
                    </Field>
                    <Field label="Type werk">
                      <input
                        value={rule.match.workType}
                        onChange={(event) => updateRuleMatch(index, "workType", event.target.value, setEditorForm)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                        placeholder="Bijv. service, montage"
                      />
                    </Field>
                    {rule.lineType === "labor" ? (
                      <Field label="Uursoort">
                        <select
                          value={rule.match.hourTypeId}
                          onChange={(event) => updateRuleMatch(index, "hourTypeId", event.target.value, setEditorForm)}
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                        >
                          <option value="">Alle uursoorten</option>
                          {hourTypes.map((hourType) => (
                            <option key={hourType._id} value={hourType._id}>
                              {hourType.code ? `${hourType.code} · ` : ""}{hourType.name}
                            </option>
                          ))}
                        </select>
                      </Field>
                    ) : null}
                    {rule.lineType === "material" ? (
                      <>
                        <Field label="Materiaalcode">
                          <input
                            value={rule.match.materialCatalogKey}
                            onChange={(event) => updateRuleMatch(index, "materialCatalogKey", event.target.value, setEditorForm)}
                            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                          />
                        </Field>
                        <Field label="Materiaalcategorie">
                          <input
                            value={rule.match.materialCategory}
                            onChange={(event) => updateRuleMatch(index, "materialCategory", event.target.value, setEditorForm)}
                            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                          />
                        </Field>
                      </>
                    ) : null}
                    {rule.lineType === "other" ? (
                      <Field label="Categorie overig">
                        <input
                          value={rule.match.otherCostCategory}
                          onChange={(event) => updateRuleMatch(index, "otherCostCategory", event.target.value, setEditorForm)}
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                        />
                      </Field>
                    ) : null}
                    <Field label="Min. hoeveelheid">
                      <input
                        type="number"
                        min="0"
                        value={rule.match.minQuantity}
                        onChange={(event) => updateRuleMatch(index, "minQuantity", event.target.value, setEditorForm)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                      />
                    </Field>
                    <Field label="Max. hoeveelheid">
                      <input
                        type="number"
                        min="0"
                        value={rule.match.maxQuantity}
                        onChange={(event) => updateRuleMatch(index, "maxQuantity", event.target.value, setEditorForm)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                      />
                    </Field>
                    <Field label="Actief">
                      <select
                        value={rule.active ? "true" : "false"}
                        onChange={(event) => updateRule(index, "active", event.target.value === "true", setEditorForm)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                      >
                        <option value="true">Ja</option>
                        <option value="false">Nee</option>
                      </select>
                    </Field>
                  </div>

                  <div className="mt-4 grid gap-4 xl:grid-cols-2">
                    <FormulaCard
                      title="Verkoopformule"
                      formula={rule.sellFormula}
                      onChange={(key, value) => updateRuleFormula(index, "sellFormula", key, value, setEditorForm)}
                    />
                    {showFinancials ? (
                      <FormulaCard
                        title="Kostformule"
                        formula={rule.costFormula}
                        onChange={(key, value) => updateRuleFormula(index, "costFormula", key, value, setEditorForm)}
                      />
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60"
              >
                <Save size={16} />
                {saving ? "Opslaan..." : "Opslaan"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      <section className="grid gap-5 xl:grid-cols-[1.35fr_1fr]">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Detail en historie</h4>
              <p className="mt-1 text-sm text-slate-600">Bekijk geldigheid, waarschuwingen, audittrail en versies binnen dezelfde serie.</p>
            </div>
            {detailLoading ? <span className="text-xs text-slate-500">Laden...</span> : null}
          </div>

          {!detail && !detailLoading ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-600">
              Kies een prijsafspraak in het overzicht om detail, waarschuwingen en versiehistorie te tonen.
            </div>
          ) : null}

          {detail?.agreement ? (
            <div className="space-y-5">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h5 className="text-lg font-semibold text-slate-950">{detail.agreement.name || "-"}</h5>
                    <p className="mt-1 text-sm text-slate-600">{detail.agreement.description || "Geen omschrijving"}</p>
                  </div>
                  <StatusBadge tone={mapDerivedStatusTone(detail?.derivedStatus?.key)} label={detail?.derivedStatus?.label || "-"} />
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <MetaItem label="Scope" value={getPricingScopeLabel(detail.agreement.scopeType)} />
                  <MetaItem label="Context" value={detail?.contextSummary?.title || "-"} />
                  <MetaItem label="Geldigheid" value={formatPricingValidity(detail.agreement)} />
                  <MetaItem label="Regels" value={String(detail.agreement.ruleCount || detail.agreement.rules?.length || 0)} />
                </div>
                <div className="mt-4 grid gap-2 text-xs text-slate-500">
                  <DeveloperRow label="Agreement-id" value={detail.agreement._id} />
                  <DeveloperRow label="Series-id" value={detail.versionSeriesId} />
                  <DeveloperRow label="Version" value={`v${detail.version || 1}`} />
                  <DeveloperRow label="Supersedes-id" value={detail.supersedesId || "-"} />
                  <DeveloperRow label="Effective from" value={detail.agreement.effectiveFrom || "-"} />
                  <DeveloperRow label="Effective to" value={detail.agreement.effectiveTo || "-"} />
                </div>
              </div>

              {Array.isArray(detail?.warnings) && detail.warnings.length > 0 ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-900">
                    <AlertTriangle size={16} />
                    Waarschuwingen
                  </div>
                  <div className="space-y-2 text-sm text-amber-900">
                    {detail.warnings.map((warning, index) => (
                      <p key={`${warning.code || "warning"}-${index}`}>{warning.message || warning.code || "Waarschuwing"}</p>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="grid gap-5 xl:grid-cols-2">
                <div>
                  <h6 className="text-sm font-semibold text-slate-900">Tariefregels</h6>
                  <div className="mt-3 space-y-3">
                    {(Array.isArray(detail.agreement.rules) ? detail.agreement.rules : []).map((rule) => (
                      <div key={rule._id || rule.name} className="rounded-xl border border-slate-200 p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="font-medium text-slate-900">{rule.name || "Prijsregel"}</p>
                            <p className="text-xs text-slate-500">{getLineTypeLabel(rule.lineType)} · prioriteit {rule.priority || 100}</p>
                          </div>
                          <StatusBadge tone={rule.active === false ? "warning" : "success"} label={rule.active === false ? "Inactief" : "Actief"} />
                        </div>
                        <div className="mt-3 grid gap-2 text-xs text-slate-600">
                          <DeveloperRow label="Match" value={describeRuleMatch(rule)} />
                          <DeveloperRow label="Verkoop" value={describeFormula(rule.sellFormula)} />
                          {showFinancials ? <DeveloperRow label="Kost" value={describeFormula(rule.costFormula)} /> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-5">
                  <div>
                    <h6 className="text-sm font-semibold text-slate-900">Versiehistorie</h6>
                    <div className="mt-3 space-y-3">
                      {(Array.isArray(detail.versionHistory) ? detail.versionHistory : []).map((item) => (
                        <button
                          type="button"
                          key={item._id}
                          onClick={() => openDetail(item._id)}
                          className="flex w-full items-start justify-between gap-3 rounded-xl border border-slate-200 px-3 py-3 text-left transition hover:bg-slate-50"
                        >
                          <div>
                            <p className="font-medium text-slate-900">{item.name || "Prijsafspraak"}</p>
                            <p className="text-xs text-slate-500">{formatPricingValidity(item)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-slate-900">v{item.version || 1}</p>
                            <StatusBadge tone={mapDerivedStatusTone(item?.derivedStatus?.key)} label={item?.derivedStatus?.label || "-"} className="mt-1" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h6 className="text-sm font-semibold text-slate-900">Verschillen t.o.v. vorige versie</h6>
                    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                      {buildVersionDiff(detail).map((item) => (
                        <p key={item}>{item}</p>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h6 className="text-sm font-semibold text-slate-900">Audittrail</h6>
                    <div className="mt-3 space-y-3">
                      {auditEntries.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
                          Nog geen auditregels gevonden voor deze serie.
                        </div>
                      ) : (
                        auditEntries.slice(0, 12).map((entry) => (
                          <div key={entry._id} className="rounded-xl border border-slate-200 p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="font-medium text-slate-900">{entry.actie || "-"}</p>
                                <p className="text-xs text-slate-500">{entry.userNaam || "Onbekend"} · {formatPricingDateTime(entry.createdAt)}</p>
                              </div>
                              <StatusBadge tone="neutral" label={entry.auditLevel || "audit"} />
                            </div>
                            <pre className="mt-3 overflow-x-auto rounded-lg bg-slate-50 p-3 text-xs text-slate-700">
                              {JSON.stringify(entry.wijzigingen || {}, null, 2)}
                            </pre>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-start gap-3">
            <div className="rounded-xl bg-slate-900 p-2 text-white">
              <ShieldAlert size={16} />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Prijs controleren</h4>
              <p className="mt-1 text-sm text-slate-600">
                Read-only preview via dezelfde pricingResolutionService als de calculatieflow. Er wordt niets gemuteerd.
              </p>
            </div>
          </div>

          <form onSubmit={runResolvePreview} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Project">
                <select
                  value={resolveForm.projectId}
                  onChange={(event) => setResolveForm((prev) => ({ ...prev, projectId: event.target.value }))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                >
                  <option value="">Geen project</option>
                  {projects.map((project) => (
                    <option key={project.id || project._id} value={project.id || project._id}>
                      {project.projectNummer ? `${project.projectNummer} · ` : ""}{project.naam || project.title}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Prijsprofiel override">
                <select
                  value={resolveForm.pricingProfileId}
                  onChange={(event) => setResolveForm((prev) => ({ ...prev, pricingProfileId: event.target.value }))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                >
                  <option value="">Gebruik klantcontext</option>
                  {profileOptions.map((option) => (
                    <option key={option._id} value={option.scopeRef || ""}>
                      {option.name} · v{option.version || 1}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Prijspeildatum">
                <input
                  type="date"
                  value={resolveForm.pricingDate}
                  onChange={(event) => setResolveForm((prev) => ({ ...prev, pricingDate: event.target.value }))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                />
              </Field>
              <Field label="Regeltype">
                <select
                  value={resolveForm.lineType}
                  onChange={(event) => setResolveForm((prev) => resetResolveFormLineType(prev, event.target.value))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                >
                  {LINE_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Hoeveelheid">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={resolveForm.quantity}
                  onChange={(event) => setResolveForm((prev) => ({ ...prev, quantity: event.target.value }))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                />
              </Field>
              <Field label="Type werk">
                <input
                  value={resolveForm.workType}
                  onChange={(event) => setResolveForm((prev) => ({ ...prev, workType: event.target.value }))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                  placeholder="Bijv. service, montage"
                />
              </Field>
              {resolveForm.lineType === "labor" ? (
                <Field label="Uursoort">
                  <select
                    value={resolveForm.hourTypeId}
                    onChange={(event) => setResolveForm((prev) => ({ ...prev, hourTypeId: event.target.value }))}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                  >
                    <option value="">Kies uursoort</option>
                    {hourTypes.map((hourType) => (
                      <option key={hourType._id} value={hourType._id}>
                        {hourType.code ? `${hourType.code} · ` : ""}{hourType.name}
                      </option>
                    ))}
                  </select>
                </Field>
              ) : null}
              {resolveForm.lineType === "material" ? (
                <>
                  <Field label="Materiaalcode">
                    <input
                      value={resolveForm.materialCatalogKey}
                      onChange={(event) => setResolveForm((prev) => ({ ...prev, materialCatalogKey: event.target.value }))}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                    />
                  </Field>
                  <Field label="Materiaalcategorie">
                    <input
                      value={resolveForm.materialCategory}
                      onChange={(event) => setResolveForm((prev) => ({ ...prev, materialCategory: event.target.value }))}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                    />
                  </Field>
                </>
              ) : null}
              {resolveForm.lineType === "other" ? (
                <Field label="Categorie overig">
                  <input
                    value={resolveForm.otherCostCategory}
                    onChange={(event) => setResolveForm((prev) => ({ ...prev, otherCostCategory: event.target.value }))}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                  />
                </Field>
              ) : null}
              {showFinancials ? (
                <Field label="Kostinput">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={resolveForm.costInput}
                    onChange={(event) => setResolveForm((prev) => ({ ...prev, costInput: event.target.value }))}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                  />
                </Field>
              ) : null}
            </div>

            <button
              type="submit"
              disabled={resolveLoading}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
            >
              <Play size={16} />
              {resolveLoading ? "Controleert..." : "Prijs controleren"}
            </button>
          </form>

          {resolveResult ? (
            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="grid gap-3 md:grid-cols-2">
                <MetaItem label="Verkoopprijs" value={formatCurrency(resolveResult.sellRate)} />
                {showFinancials ? <MetaItem label="Kostprijs" value={formatCurrency(resolveResult.costRate)} /> : null}
                {showFinancials ? <MetaItem label="Marge" value={formatCurrency(resolveResult.margin)} /> : null}
                <MetaItem label="Versie" value={resolveResult.agreementVersion ? `v${resolveResult.agreementVersion}` : "-"} />
                <MetaItem label="Verkoopbron" value={describeSource(resolveResult.sellSource)} />
                {showFinancials ? <MetaItem label="Kostbron" value={describeSource(resolveResult.costSource)} /> : null}
              </div>
              <div className="mt-4 rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-700">
                <p className="font-semibold text-slate-900">Resolution trace</p>
                <pre className="mt-2 overflow-x-auto">{JSON.stringify(resolveResult.resolutionTrace || [], null, 2)}</pre>
              </div>
              {Array.isArray(resolveResult.warnings) && resolveResult.warnings.length > 0 ? (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  {resolveResult.warnings.map((warning, index) => (
                    <p key={`${warning}-${index}`}>{warning}</p>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </section>
      </section>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      {children}
    </label>
  );
}

function StatCard({ label, value, tone = "neutral" }) {
  const toneMap = {
    neutral: "border-slate-200 bg-slate-50 text-slate-900",
    success: "border-emerald-200 bg-emerald-50 text-emerald-900",
    warning: "border-amber-200 bg-amber-50 text-amber-900",
    danger: "border-rose-200 bg-rose-50 text-rose-900",
  };

  return (
    <div className={`rounded-2xl border px-4 py-4 ${toneMap[tone] || toneMap.neutral}`}>
      <p className="text-xs font-semibold uppercase tracking-[0.18em] opacity-70">{label}</p>
      <p className="mt-2 text-2xl font-semibold">{value}</p>
    </div>
  );
}

function MetaItem({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-sm text-slate-900">{value || "-"}</p>
    </div>
  );
}

function DeveloperRow({ label, value }) {
  return (
    <div className="grid gap-2 sm:grid-cols-[120px_1fr]">
      <span className="font-semibold text-slate-500">{label}</span>
      <span className="break-all">{String(value || "-")}</span>
    </div>
  );
}

function FormulaCard({ title, formula, onChange }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <h6 className="text-sm font-semibold text-slate-900">{title}</h6>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <Field label="Modus">
          <select
            value={formula.mode}
            onChange={(event) => onChange("mode", event.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
          >
            {FORMULA_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Waarde">
          <input
            type="number"
            step="0.01"
            value={formula.value}
            onChange={(event) => onChange("value", event.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
          />
        </Field>
        <Field label="Minimumbedrag">
          <input
            type="number"
            step="0.01"
            value={formula.minimumAmount}
            onChange={(event) => onChange("minimumAmount", event.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
          />
        </Field>
        <Field label="Label">
          <input
            value={formula.label}
            onChange={(event) => onChange("label", event.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
          />
        </Field>
      </div>
    </div>
  );
}

function buildAgreementForm({ clientId }) {
  return {
    id: "",
    name: "",
    description: "",
    scopeType: "client",
    scopeRef: clientId || "",
    currency: "EUR",
    effectiveFrom: "",
    effectiveTo: "",
    rules: [cloneRule(EMPTY_RULE)],
  };
}

function buildAgreementFormFromAgreement(agreement = {}) {
  return {
    id: String(agreement._id || ""),
    name: agreement.name || "",
    description: agreement.description || "",
    scopeType: agreement.scopeType || "client",
    scopeRef: String(agreement.scopeRef || ""),
    currency: agreement.currency || "EUR",
    effectiveFrom: agreement.effectiveFrom ? String(agreement.effectiveFrom).slice(0, 10) : "",
    effectiveTo: agreement.effectiveTo ? String(agreement.effectiveTo).slice(0, 10) : "",
    rules: (Array.isArray(agreement.rules) && agreement.rules.length > 0 ? agreement.rules : [EMPTY_RULE]).map((rule) => ({
      localId: `${rule._id || Math.random().toString(16).slice(2)}`,
      id: String(rule._id || ""),
      name: rule.name || "",
      code: rule.code || "",
      lineType: rule.lineType || "labor",
      active: rule.active !== false,
      priority: String(rule.priority ?? 100),
      match: {
        workType: rule?.match?.workType || "",
        hourTypeId: String(rule?.match?.hourTypeId || ""),
        materialCatalogKey: rule?.match?.materialCatalogKey || "",
        materialCategory: rule?.match?.materialCategory || "",
        otherCostCategory: rule?.match?.otherCostCategory || "",
        minQuantity: rule?.match?.minQuantity ?? "",
        maxQuantity: rule?.match?.maxQuantity ?? "",
      },
      sellFormula: {
        mode: rule?.sellFormula?.mode || "inherit",
        value: rule?.sellFormula?.value ?? "",
        minimumAmount: rule?.sellFormula?.minimumAmount ?? "",
        label: rule?.sellFormula?.label || "",
      },
      costFormula: {
        mode: rule?.costFormula?.mode || "inherit",
        value: rule?.costFormula?.value ?? "",
        minimumAmount: rule?.costFormula?.minimumAmount ?? "",
        label: rule?.costFormula?.label || "",
      },
      notes: rule.notes || "",
    })),
  };
}

function serializeAgreementForm(form, clientId) {
  const scopeType = String(form.scopeType || "client").trim();
  const payload = {
    name: String(form.name || "").trim(),
    description: String(form.description || "").trim(),
    scopeType,
    currency: String(form.currency || "EUR").trim().toUpperCase() || "EUR",
    effectiveFrom: form.effectiveFrom || undefined,
    effectiveTo: form.effectiveTo || undefined,
    rules: (Array.isArray(form.rules) ? form.rules : []).map((rule) => ({
      ...(rule.id ? { _id: rule.id } : {}),
      name: String(rule.name || "").trim() || "Prijsregel",
      code: String(rule.code || "").trim(),
      lineType: String(rule.lineType || "labor").trim(),
      active: rule.active !== false,
      priority: Number(rule.priority || 100),
      match: {
        workType: String(rule?.match?.workType || "").trim(),
        hourTypeId: rule?.match?.hourTypeId || undefined,
        materialCatalogKey: String(rule?.match?.materialCatalogKey || "").trim(),
        materialCategory: String(rule?.match?.materialCategory || "").trim(),
        otherCostCategory: String(rule?.match?.otherCostCategory || "").trim(),
        minQuantity: rule?.match?.minQuantity === "" ? undefined : Number(rule.match.minQuantity),
        maxQuantity: rule?.match?.maxQuantity === "" ? undefined : Number(rule.match.maxQuantity),
      },
      sellFormula: serializeFormula(rule.sellFormula),
      costFormula: serializeFormula(rule.costFormula),
      notes: String(rule.notes || "").trim(),
    })),
  };

  if (scopeType === "client") {
    payload.scopeRef = clientId;
  } else if (scopeType === "project") {
    payload.scopeRef = form.scopeRef || undefined;
  }

  return payload;
}

function serializeFormula(formula = {}) {
  return {
    mode: String(formula.mode || "inherit").trim(),
    value: formula.value === "" ? 0 : Number(formula.value || 0),
    minimumAmount: formula.minimumAmount === "" ? undefined : Number(formula.minimumAmount),
    label: String(formula.label || "").trim(),
  };
}

function cloneRule(rule = {}) {
  return {
    localId: `rule-${Math.random().toString(16).slice(2)}`,
    id: "",
    name: rule.name || "",
    code: rule.code || "",
    lineType: rule.lineType || "labor",
    active: rule.active !== false,
    priority: String(rule.priority ?? 100),
    match: {
      workType: rule?.match?.workType || "",
      hourTypeId: rule?.match?.hourTypeId || "",
      materialCatalogKey: rule?.match?.materialCatalogKey || "",
      materialCategory: rule?.match?.materialCategory || "",
      otherCostCategory: rule?.match?.otherCostCategory || "",
      minQuantity: rule?.match?.minQuantity ?? "",
      maxQuantity: rule?.match?.maxQuantity ?? "",
    },
    sellFormula: {
      mode: rule?.sellFormula?.mode || "inherit",
      value: rule?.sellFormula?.value ?? "",
      minimumAmount: rule?.sellFormula?.minimumAmount ?? "",
      label: rule?.sellFormula?.label || "",
    },
    costFormula: {
      mode: rule?.costFormula?.mode || "inherit",
      value: rule?.costFormula?.value ?? "",
      minimumAmount: rule?.costFormula?.minimumAmount ?? "",
      label: rule?.costFormula?.label || "",
    },
    notes: rule.notes || "",
  };
}

function updateScopeType(form, scopeType, clientId) {
  if (scopeType === "client") {
    return { ...form, scopeType, scopeRef: clientId };
  }
  if (scopeType === "tenant_default" || scopeType === "pricing_profile") {
    return { ...form, scopeType, scopeRef: "" };
  }
  return { ...form, scopeType, scopeRef: "" };
}

function updateRule(index, key, value, setEditorForm) {
  setEditorForm((prev) => ({
    ...prev,
    rules: prev.rules.map((rule, ruleIndex) => (
      ruleIndex === index
        ? {
            ...rule,
            [key]: value,
          }
        : rule
    )),
  }));
}

function updateRuleMatch(index, key, value, setEditorForm) {
  setEditorForm((prev) => ({
    ...prev,
    rules: prev.rules.map((rule, ruleIndex) => (
      ruleIndex === index
        ? {
            ...rule,
            match: {
              ...rule.match,
              [key]: value,
            },
          }
        : rule
    )),
  }));
}

function updateRuleFormula(index, formulaKey, key, value, setEditorForm) {
  setEditorForm((prev) => ({
    ...prev,
    rules: prev.rules.map((rule, ruleIndex) => (
      ruleIndex === index
        ? {
            ...rule,
            [formulaKey]: {
              ...rule[formulaKey],
              [key]: value,
            },
          }
        : rule
    )),
  }));
}

function mapDerivedStatusTone(statusKey = "") {
  switch (String(statusKey || "").trim().toLowerCase()) {
    case "active":
      return "success";
    case "expired":
      return "warning";
    case "archived":
      return "danger";
    default:
      return "neutral";
  }
}

function getLineTypeLabel(lineType = "") {
  return LINE_TYPE_OPTIONS.find((option) => option.value === lineType)?.label || lineType || "-";
}

function describeRuleMatch(rule = {}) {
  const parts = [];
  if (rule?.match?.workType) parts.push(`workType=${rule.match.workType}`);
  if (rule?.match?.hourTypeId) parts.push(`hourType=${rule.match.hourTypeId}`);
  if (rule?.match?.materialCatalogKey) parts.push(`catalogKey=${rule.match.materialCatalogKey}`);
  if (rule?.match?.materialCategory) parts.push(`materiaalgroep=${rule.match.materialCategory}`);
  if (rule?.match?.otherCostCategory) parts.push(`overig=${rule.match.otherCostCategory}`);
  if (rule?.match?.minQuantity !== undefined && rule?.match?.minQuantity !== null && rule?.match?.minQuantity !== "") {
    parts.push(`min=${rule.match.minQuantity}`);
  }
  if (rule?.match?.maxQuantity !== undefined && rule?.match?.maxQuantity !== null && rule?.match?.maxQuantity !== "") {
    parts.push(`max=${rule.match.maxQuantity}`);
  }
  return parts.length > 0 ? parts.join(" · ") : "Algemene fallbackregel";
}

function describeFormula(formula = {}) {
  if (!formula) return "-";
  return [
    String(formula.mode || "inherit"),
    formula.value !== undefined && formula.value !== null && formula.value !== "" ? `waarde=${formula.value}` : "",
    formula.minimumAmount !== undefined && formula.minimumAmount !== null && formula.minimumAmount !== ""
      ? `minimum=${formula.minimumAmount}`
      : "",
    formula.label ? `label=${formula.label}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

function buildVersionDiff(detail = {}) {
  const history = Array.isArray(detail?.versionHistory) ? detail.versionHistory : [];
  if (history.length < 2) return ["Nog geen vorige versie in deze serie."];

  const current = detail.agreement || history[0];
  const previous = history.find((item) => String(item._id) !== String(current._id));
  if (!previous) return ["Nog geen vorige versie in deze serie."];

  const lines = [];
  if (String(current.name || "") !== String(previous.name || "")) {
    lines.push(`Naam gewijzigd van "${previous.name || "-"}" naar "${current.name || "-"}".`);
  }
  if (String(current.description || "") !== String(previous.description || "")) {
    lines.push("Omschrijving gewijzigd.");
  }
  if (String(current.effectiveFrom || "") !== String(previous.effectiveFrom || "") || String(current.effectiveTo || "") !== String(previous.effectiveTo || "")) {
    lines.push(`Geldigheid gewijzigd: ${formatPricingValidity(previous)} -> ${formatPricingValidity(current)}.`);
  }

  const currentCount = Array.isArray(current.rules) ? current.rules.length : Number(current.ruleCount || 0);
  const previousCount = Array.isArray(previous.rules) ? previous.rules.length : Number(previous.ruleCount || 0);
  if (currentCount !== previousCount) {
    lines.push(`Aantal regels gewijzigd van ${previousCount} naar ${currentCount}.`);
  }

  return lines.length > 0 ? lines : ["Geen eenvoudige veldwijzigingen gedetecteerd; controleer de detailregels en audittrail."];
}

export function buildResolvePayload({ clientId, assignedProfileScopeRef, form, includeFinancials = true }) {
  const lineType = String(form.lineType || "labor").trim();
  const payload = {
    clientId,
    projectId: form.projectId || undefined,
    pricingProfileId: form.pricingProfileId || assignedProfileScopeRef || undefined,
    pricingDate: form.pricingDate || undefined,
    lineType,
    line: {
      quantity: Number(form.quantity || 0),
      workType: String(form.workType || "").trim(),
      discount: form.discount === "" ? undefined : Number(form.discount),
      markup: form.markup === "" ? undefined : Number(form.markup),
    },
  };

  if (includeFinancials && form.costInput !== "") {
    payload.line.costInput = Number(form.costInput);
  }

  if (lineType === "labor") {
    payload.line.hourTypeId = form.hourTypeId || undefined;
    if (form.sellRate !== "") payload.line.sellRate = Number(form.sellRate);
    if (includeFinancials && form.costRate !== "") payload.line.costRate = Number(form.costRate);
  } else if (lineType === "material") {
    payload.line.catalogKey = String(form.materialCatalogKey || "").trim();
    payload.line.categoryKey = String(form.materialCategory || "").trim();
    if (form.sellRate !== "") payload.line.sellRate = Number(form.sellRate);
    if (includeFinancials && form.costRate !== "") payload.line.costRate = Number(form.costRate);
  } else {
    payload.line.categoryKey = String(form.otherCostCategory || "").trim();
    if (form.amountSell !== "") payload.line.amountSell = Number(form.amountSell);
    if (includeFinancials && form.amountCost !== "") payload.line.amountCost = Number(form.amountCost);
  }

  return payload;
}

function resetResolveFormLineType(currentForm, lineType) {
  return {
    ...EMPTY_RESOLVE_FORM,
    pricingDate: currentForm.pricingDate,
    projectId: currentForm.projectId,
    pricingProfileId: currentForm.pricingProfileId,
    lineType,
  };
}

function formatCurrency(value) {
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function describeSource(source = null) {
  if (!source) return "-";
  const scopeLabel = getPricingScopeLabel(source.scopeType || source.sourceType || "");
  if (source.agreementVersion) {
    return `${scopeLabel} · v${source.agreementVersion}`;
  }
  return scopeLabel;
}
